import { numberLocale } from './language';

export interface PlanInfo {
  tier: string;
  name: string;
  lookup: string;
  priceEUR: string;
  priceUSD: string;
  yearlyPriceEUR: string;
  yearlyPriceUSD: string;
  productLimit: number;
  features: string[];
  featureFlags: {
    aiTitleDescription: boolean;
    mcConnect: boolean;
    feedHosting: boolean;
    customPrompts: boolean;
    multiMC: boolean;
    multiLang: boolean;
    prioritySupport: boolean;
    dedicatedManager: boolean;
  };
}

/**
 * Preisumbau 2026-08: aus 6 Tarifen werden Kapazitaets-Baender. Ein Band kauft
 * Betreuungs-Kapazitaet (Bestandsmass, `check_can_optimize` v4.1), keine
 * Feature-Stufen — alle Baender tragen denselben Funktionsumfang, nur
 * Support-Prioritaet und die inkludierten Merchant-Center/Zielsprachen
 * staffeln sich (Add-on-Regeln: `supabase/functions/_shared/plan-features.ts`).
 *
 * Bindende Staffel (EUR; USD 1:1; Jahr = Monat x 12 x 0,8 gerundet):
 * cap500 19/182 · cap1000 29/278 · cap1500 39/374 · cap2000 49/470 ·
 * cap5000 89/854 · cap10000 149/1430 · cap25000 229/2198 ·
 * cap50000 349/3350 · cap100000 499/4790.
 *
 * WICHTIG: `yearlyPrice*` ist bei den Baendern der volle JAHRES-Betrag —
 * anders als bei den Legacy-Tarifen, deren Jahreswert historisch der
 * Pro-Monat-Aequivalenzpreis war. `planPrice(plan, currency, 'yearly')`
 * liefert also fuer Baender den Jahresbetrag.
 */
type CapBandSpec = {
  size: number;
  monthly: number;
  yearly: number;
};

const CAP_BANDS: readonly CapBandSpec[] = [
  { size: 500, monthly: 19, yearly: 182 },
  { size: 1_000, monthly: 29, yearly: 278 },
  { size: 1_500, monthly: 39, yearly: 374 },
  { size: 2_000, monthly: 49, yearly: 470 },
  { size: 5_000, monthly: 89, yearly: 854 },
  { size: 10_000, monthly: 149, yearly: 1_430 },
  { size: 25_000, monthly: 229, yearly: 2_198 },
  { size: 50_000, monthly: 349, yearly: 3_350 },
  { size: 100_000, monthly: 499, yearly: 4_790 },
] as const;

/** `true` fuer die kaufbaren Kapazitaets-Baender (`cap500` … `cap100000`). */
export function isCapTier(tier: string | undefined | null): boolean {
  return typeof tier === 'string' && tier.startsWith('cap');
}

export type BillingCycle = 'monthly' | 'yearly';

/**
 * Strukturelle Untermenge von `Subscription` (hooks/useSubscription) — nur die
 * Felder, die die Abrechnungs-Entscheidungen hier brauchen. Bewusst strukturell,
 * damit lib/plans.ts frei von Supabase-Imports bleibt (pure Funktionen).
 */
export type SubscriptionLike = {
  plan_tier?: string | null;
  status?: string | null;
  billing_interval?: string | null;
  product_limit?: number | null;
  stripe_subscription_id?: string | null;
} | null | undefined;

/**
 * Kapazitaet des Free-Tarifs. ⚠ SYNC REQUIRED mit FREE_PRODUCT_LIMIT in
 * supabase/functions/_shared/plan-features.ts (Drift-Waechter:
 * supabase/functions/_shared/plan-features.mirror.test.ts).
 */
export const FREE_PRODUCT_LIMIT = 200;

/** Kontakt fuer Sondertarife und alles, was sich nicht selbst buchen laesst. */
export const SUPPORT_EMAIL = 'support@feedoptimizer.ai';

/**
 * Stripe-Status, in denen ein Abo lebt. ⚠ SYNC REQUIRED mit
 * `hasLiveSubscription` in supabase/functions/create-checkout/checkout-keys.ts.
 */
const LIVE_SUBSCRIPTION_STATUSES: readonly string[] = ['active', 'trialing', 'past_due'];

/**
 * Sondertarif (D7). Spiegel von `isSpecialPlan` in
 * supabase/functions/_shared/plan-features.ts; die Begruendung steht dort.
 */
export function isSpecialPlan(
  planTier: string | null | undefined,
  productLimit: number | null | undefined,
): boolean {
  if (!planTier) return false;
  const limit = typeof productLimit === 'number' && Number.isFinite(productLimit) ? productLimit : null;
  if (planTier === 'free') return limit !== null && limit > FREE_PRODUCT_LIMIT;
  const m = /^cap(\d+)$/.exec(planTier);
  if (m && Number(m[1]) > 0) return limit !== null && limit > Number(m[1]);
  return true;
}

/** Lebendes Stripe-Abo (ID vorhanden, Status aktiv, Test oder ueberfaellig). */
export function hasLiveStripeSubscription(subscription: SubscriptionLike): boolean {
  return !!subscription?.stripe_subscription_id
    && LIVE_SUBSCRIPTION_STATUSES.includes(subscription.status ?? '');
}

export type BillingAccountKind = 'free' | 'subscriber' | 'special';

/**
 * Die EINE Einordnung eines Kontos fuer alle Kauf-Flaechen (UX-Audit K37/K38):
 *   - `special`:    Sondertarif (D7), Kontakt statt Kauf.
 *   - `subscriber`: Band mit lebendem Stripe-Abo, Bandwechsel ueber
 *                   manage-subscription, nie ein zweiter Checkout.
 *   - `free`:       alles andere, Neuabschluss ueber den Checkout.
 * Vorher galt jedes nicht-freie, nicht gekuendigte Konto als Bestandskunde,
 * auch ohne Stripe-Abo. Ein Agenturkonto landete so im Bandwechsel, bekam 409
 * NO_ACTIVE_SUBSCRIPTION und fiel still in einen Neukunden-Checkout.
 */
export function billingAccountKind(subscription: SubscriptionLike): BillingAccountKind {
  if (!subscription) return 'free';
  if (isSpecialPlan(subscription.plan_tier, subscription.product_limit)) return 'special';
  if (subscription.plan_tier && subscription.plan_tier !== 'free' && hasLiveStripeSubscription(subscription)) {
    return 'subscriber';
  }
  return 'free';
}

/**
 * Lebendes bezahltes Band-Abo bei Stripe: die EINE Definition von
 * "Bestandskunde". Seit dem UX-Audit (K36) ohne Stripe-Abo nie wahr.
 */
export function hasPaidSubscription(subscription: SubscriptionLike): boolean {
  return billingAccountKind(subscription) === 'subscriber';
}

export type UpgradeRoute = 'checkout' | 'bandChange' | 'contact' | 'owner';

/**
 * Wohin fuehrt "mehr Kapazitaet"? Ein Mitglied einer fremden Merchant Center
 * kann das Kontingent des Eigentuemers nicht kaufen, ein Sondertarif nicht
 * selbst aendern, ein Abonnent wechselt das Band, alle anderen kaufen neu.
 */
export function upgradeRoute(kind: BillingAccountKind, isMemberOfForeignMc: boolean): UpgradeRoute {
  if (isMemberOfForeignMc) return 'owner';
  if (kind === 'special') return 'contact';
  if (kind === 'subscriber') return 'bandChange';
  return 'checkout';
}

/**
 * Vorausgewaehltes Abrechnungsintervall fuer Kauf-Flaechen (CRO-Entscheidung
 * Dennis, 21.08.2026): bei NEUABSCHLUESSEN ist die Jahresabrechnung Standard.
 * Bestandskunden behalten ihr aktuelles Intervall als Vorauswahl — ein
 * monthly↔yearly-Wechsel bleibt eine bewusste Nutzer-Aktion (bzw. Portal/
 * Support, siehe PlanBandManager). Waehrend das Abo noch laedt (undefined)
 * gilt der Neuabschluss-Default; der planCache-Seed macht das Fenster minimal.
 */
export function defaultBillingCycle(subscription: SubscriptionLike): BillingCycle {
  if (!hasPaidSubscription(subscription)) return 'yearly';
  return subscription?.billing_interval === 'yearly' ? 'yearly' : 'monthly';
}

/**
 * Stripe-Lookup-Key eines Plans fuer den gewaehlten Zyklus. Zentralisiert das
 * vorher dreifach duplizierte `.replace('_monthly', '_yearly')` (PlanCard,
 * PlanBandManager, Pricing). Fuer Free (`lookup: ''`) ein No-op; fuer
 * Checkout-CTAs nur mit Kapazitaets-Baendern verwenden — Legacy-Tarife sind
 * nicht mehr kaufbar und ihre `_yearly`-Keys sind nicht garantiert.
 */
export function lookupForCycle(plan: Pick<PlanInfo, 'lookup'>, cycle: BillingCycle): string {
  return cycle === 'yearly' ? plan.lookup.replace('_monthly', '_yearly') : plan.lookup;
}

/**
 * Umkehrung von `lookupForCycle` fuer die Kapazitaets-Baender (A7-NEU-2): aus
 * "cap500_yearly" werden Band und Intervall. Legacy-Tarife, Add-ons und alles
 * Unbekannte liefern null; der B2B-Dialog zeigt dann keine Kaufzeile.
 */
export function planFromLookupKey(lookupKey: string): { plan: PlanInfo; cycle: BillingCycle } | null {
  const match = /^(.+)_(monthly|yearly)$/.exec(lookupKey);
  if (!match) return null;
  const plan = PLANS_BY_TIER[match[1]];
  if (!plan || !isCapTier(plan.tier)) return null;
  return { plan, cycle: match[2] as BillingCycle };
}

/** en-US-Gruppierung als neutrale Fallback-Schreibweise ('1,000'). */
const enAmount = (n: number) => n.toLocaleString('en-US');

function capBandFeatures(size: number): string[] {
  const features = ['everythingInFree', 'automationDrift', 'customPrompts'];
  if (size >= 25_000) features.push('unlimitedMcLang');
  else if (size >= 5_000) features.push('threeMcLang');
  if (size >= 50_000) features.push('prioritySupport');
  return features;
}

function capBandPlan({ size, monthly, yearly }: CapBandSpec): PlanInfo {
  return {
    tier: `cap${size}`,
    // Englischer Fallback-Name (Analytics, defaultValue). Die UI zeigt Baender
    // ueber `planDisplayName` lokalisiert als "1.000 Produkte" / "1,000 products".
    name: `${enAmount(size)} products`,
    lookup: `cap${size}_monthly`,
    priceEUR: `€${enAmount(monthly)}`,
    priceUSD: `$${enAmount(monthly)}`,
    yearlyPriceEUR: `€${enAmount(yearly)}`,
    yearlyPriceUSD: `$${enAmount(yearly)}`,
    productLimit: size,
    features: capBandFeatures(size),
    featureFlags: {
      aiTitleDescription: true,
      mcConnect: true,
      feedHosting: true,
      customPrompts: true,
      // Mehrere MCs/Sprachen sind ab cap5000 inklusive (3 bzw. unbegrenzt ab
      // cap25000); darunter laufen sie als 9-€-Add-ons ueber `manage-subscription`.
      multiMC: size >= 5_000,
      multiLang: size >= 5_000,
      prioritySupport: size >= 50_000,
      dedicatedManager: false,
    },
  };
}

/**
 * Oeffentliche Auswahl: Free + die 9 Kapazitaets-Baender. Aufsteigend nach
 * `productLimit` sortiert — `getRecommendedPlan`/`getNextPlan` verlassen sich
 * auf diese Ordnung.
 */
export const PLANS: PlanInfo[] = [
  {
    tier: 'free',
    name: 'Free',
    lookup: '',
    priceEUR: '€0',
    priceUSD: '$0',
    yearlyPriceEUR: '€0',
    yearlyPriceUSD: '$0',
    productLimit: 200,
    // Free bleibt voll nutzbar fuer den Kern: manuell optimieren + hochladen.
    // Bezahlt sind kuenftig Automatik, Drift-Nachzug und Custom Prompts.
    features: ['aiTitleDescription', 'manualOptimizeUpload', 'oneMcOneLang'],
    featureFlags: {
      aiTitleDescription: true,
      mcConnect: true,
      // Free darf hochladen — der Upload-Pfad ist Teil des freien Kernpfads.
      feedHosting: true,
      customPrompts: false,
      multiMC: false,
      multiLang: false,
      prioritySupport: false,
      dedicatedManager: false,
    },
  },
  ...CAP_BANDS.map(capBandPlan),
];

/**
 * PRIVATE Legacy-Stufen (nicht mehr waehl-/kaufbar): bestehende Abos zeigen
 * weiter ihren Namen und ihre Werte, aber kein CTA bietet sie mehr an. Die
 * Werte sind eingefroren auf dem Stand vor dem Preisumbau.
 */
export const LEGACY_PLANS: PlanInfo[] = [
  {
    tier: 'starter',
    name: 'Starter',
    lookup: 'starter_monthly',
    priceEUR: '€49',
    priceUSD: '$49',
    yearlyPriceEUR: '€39',
    yearlyPriceUSD: '$39',
    productLimit: 2_000,
    features: ['everythingInFree', 'feedHosting', 'customPrompts'],
    featureFlags: {
      aiTitleDescription: true,
      mcConnect: true,
      feedHosting: true,
      customPrompts: true,
      multiMC: false,
      multiLang: false,
      prioritySupport: false,
      dedicatedManager: false,
    },
  },
  {
    tier: 'business',
    name: 'Business',
    lookup: 'business_monthly',
    priceEUR: '€149',
    priceUSD: '$149',
    yearlyPriceEUR: '€119',
    yearlyPriceUSD: '$119',
    productLimit: 10_000,
    features: ['everythingInStarter', 'multipleMerchantCenters', 'multiLanguage'],
    featureFlags: {
      aiTitleDescription: true,
      mcConnect: true,
      feedHosting: true,
      customPrompts: true,
      multiMC: true,
      multiLang: true,
      prioritySupport: false,
      dedicatedManager: false,
    },
  },
  {
    tier: 'professional',
    name: 'Professional',
    lookup: 'pro_monthly',
    priceEUR: '€349',
    priceUSD: '$349',
    yearlyPriceEUR: '€279',
    yearlyPriceUSD: '$279',
    productLimit: 50_000,
    features: ['everythingInBusiness', 'prioritySupport'],
    featureFlags: {
      aiTitleDescription: true,
      mcConnect: true,
      feedHosting: true,
      customPrompts: true,
      multiMC: true,
      multiLang: true,
      prioritySupport: true,
      dedicatedManager: false,
    },
  },
  {
    tier: 'enterprise',
    name: 'Enterprise',
    lookup: 'enterprise_monthly',
    priceEUR: '€699',
    priceUSD: '$699',
    yearlyPriceEUR: '€559',
    yearlyPriceUSD: '$559',
    productLimit: 200_000,
    features: ['everythingInProfessional', 'dedicatedAccountManager'],
    featureFlags: {
      aiTitleDescription: true,
      mcConnect: true,
      feedHosting: true,
      customPrompts: true,
      multiMC: true,
      multiLang: true,
      prioritySupport: true,
      dedicatedManager: true,
    },
  },
  {
    tier: 'agency',
    name: 'Agency',
    lookup: 'agency_monthly',
    priceEUR: '€999',
    priceUSD: '$999',
    yearlyPriceEUR: '€799',
    yearlyPriceUSD: '$799',
    productLimit: Infinity,
    features: ['everythingInEnterprise', 'unlimitedProducts'],
    featureFlags: {
      aiTitleDescription: true,
      mcConnect: true,
      feedHosting: true,
      customPrompts: true,
      multiMC: true,
      multiLang: true,
      prioritySupport: true,
      dedicatedManager: true,
    },
  },
];

/**
 * Anzeige-Aufloesung fuer JEDEN bekannten `plan_tier`-Wert aus der DB:
 * Free + Baender (oeffentlich) + Legacy (nur Anzeige bestehender Abos).
 */
export const PLANS_BY_TIER: Record<string, PlanInfo> = Object.fromEntries(
  [...PLANS, ...LEGACY_PLANS].map((p) => [p.tier, p]),
);

/**
 * i18n key (namespace `common`) for the bare "no ceiling" word. Only correct
 * where the label stands completely on its own — the product row of the plan
 * comparison table. Anywhere the label is followed by the noun, use
 * `formatProductsPhrase` instead: German "Unbegrenzt" is an adverb and does not
 * agree with "Produkte".
 */
export const UNLIMITED_PRODUCTS_KEY = 'units.unlimited';

/** i18n key (namespace `common`) for the finite "<n> products" noun phrase. */
export const PRODUCTS_COUNT_KEY = 'units.productsCount';

/** i18n key (namespace `common`) for the unlimited "products" noun phrase. */
export const PRODUCTS_UNLIMITED_KEY = 'units.productsUnlimited';

/** Minimal shape of the `t` function from `useTranslation()`. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * R2-1: the plan table used to carry a second, hard-coded, English-formatted
 * copy of the product limit (`productsLabel: '10,000'`), so the German UI
 * printed "10,000" instead of "10.000" and "Unlimited" stayed untranslated.
 * The number now lives exactly once (`productLimit`) and is formatted here at
 * render time from the active app language.
 *
 * Kept free of any i18n import so it stays a pure function: the caller passes
 * the language and its own `t` (which resolves the unlimited label from the
 * `common` namespace).
 */
export function formatProductLimit(
  plan: Pick<PlanInfo, 'productLimit'>,
  language: string | undefined,
  translate: Translate,
): string {
  if (!Number.isFinite(plan.productLimit)) {
    return translate(UNLIMITED_PRODUCTS_KEY, { ns: 'common' });
  }
  return plan.productLimit.toLocaleString(numberLocale(language));
}

/**
 * The complete noun phrase for a plan's capacity: "10.000 Produkte" /
 * "Unbegrenzte Produkte", "10,000 products" / "Unlimited products".
 *
 * I1: the five sentences that advertise a plan's capacity used to hard-code the
 * noun ("{{label}} Produkte") and splice in only the bare quantity. That reads
 * fine for a number but not for the unlimited tier — German needs adjective
 * agreement, so `units.unlimited` ("Unbegrenzt") produced "Unbegrenzt Produkte"
 * two lines above the correct "Unbegrenzte Produkte" in the same plan card.
 *
 * The noun therefore lives inside the interpolated value now: the host sentences
 * splice in a finished phrase and their grammar no longer depends on it. Any new
 * call site must keep that contract — the phrase is capitalised, so its slot has
 * to be a label or the start of a sentence, never mid-sentence, where German
 * would need the lowercase adjective ("für unbegrenzte Produkte").
 */
export function formatProductsPhrase(
  plan: Pick<PlanInfo, 'productLimit'>,
  language: string | undefined,
  translate: Translate,
): string {
  if (!Number.isFinite(plan.productLimit)) {
    return translate(PRODUCTS_UNLIMITED_KEY, { ns: 'common' });
  }
  return translate(PRODUCTS_COUNT_KEY, {
    ns: 'common',
    amount: formatProductLimit(plan, language, translate),
  });
}

/**
 * Anzeigename eines Tarifs. Kapazitaets-Baender heissen in der UI wie ihre
 * Kapazitaet ("1.000 Produkte" / "1,000 products", lokalisiert ueber die
 * `units.productsCount`-Phrase im `common`-Namespace — dieselbe Quelle wie die
 * `planNames.cap*`-Eintraege in pricing.json). Free und die Legacy-Tarife
 * behalten ihre Eigennamen ("Free", "Starter", …) — Eigennamen werden nicht
 * uebersetzt.
 */
export function planDisplayName(
  plan: Pick<PlanInfo, 'tier' | 'name' | 'productLimit'>,
  language: string | undefined,
  translate: Translate,
): string {
  if (isCapTier(plan.tier)) {
    return formatProductsPhrase(plan, language, translate);
  }
  return plan.name;
}

/**
 * Anzeigename fuer einen rohen `plan_tier`-String aus der DB. Ersetzt das
 * fruehere `plan.charAt(0).toUpperCase() + plan.slice(1)`-Muster (Settings,
 * UsageWidget, UsageIndicator, UpgradeModal, PostFetchRecommendation), das fuer
 * Band-Tiers "Cap1000" gedruckt haette. Unbekannte Tiers fallen auf den rohen
 * Wert zurueck.
 */
export function planTierLabel(
  tier: string,
  language: string | undefined,
  translate: Translate,
): string {
  const plan = PLANS_BY_TIER[tier];
  if (!plan) return tier;
  return planDisplayName(plan, language, translate);
}

/**
 * Numerischer Preis eines Tarifs. Ersetzt die sechsfach duplizierte
 * `currency === 'eur' ? plan.priceEUR : plan.priceUSD`-Ternary in den CTAs.
 *
 * Achtung Zyklus-Semantik: fuer Baender ist `'yearly'` der volle Jahresbetrag
 * (bindende Staffel), fuer die Legacy-Tarife historisch der
 * Pro-Monat-Aequivalenzpreis — die Werte kommen unveraendert aus den
 * `PlanInfo`-Strings.
 */
export function planPrice(
  plan: Pick<PlanInfo, 'priceEUR' | 'priceUSD' | 'yearlyPriceEUR' | 'yearlyPriceUSD'>,
  currency: 'eur' | 'usd',
  cycle: 'monthly' | 'yearly',
): number {
  const raw = cycle === 'monthly'
    ? (currency === 'eur' ? plan.priceEUR : plan.priceUSD)
    : (currency === 'eur' ? plan.yearlyPriceEUR : plan.yearlyPriceUSD);
  return parseInt(raw.replace(/[^0-9]/g, ''), 10) || 0;
}

/**
 * Geldbetrag ohne Nachkommastellen in der App-Sprache (UX-Audit A7O-05):
 * DE "1.068 €", EN "€1,068" / "$1,068". Vorher stand im DE-UI "€1,068" neben
 * "811.414 Produkte" und "9 €" daneben. Ohne Sprache: en-US.
 */
export function formatMoney(
  amount: number,
  currency: 'eur' | 'usd',
  language?: string | null,
): string {
  return new Intl.NumberFormat(numberLocale(language ?? 'en'), {
    style: 'currency',
    currency: currency === 'eur' ? 'EUR' : 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Anzeigepreis eines Tarifs. Ohne `language` im bisherigen en-US-Format
 * ("€29", "$1,430"), damit Aufrufer ausserhalb dieses Pakets unveraendert
 * bleiben (Rest-Stellen: WP13, K27).
 */
export function formatPlanPrice(
  plan: Pick<PlanInfo, 'priceEUR' | 'priceUSD' | 'yearlyPriceEUR' | 'yearlyPriceUSD'>,
  currency: 'eur' | 'usd',
  cycle: 'monthly' | 'yearly',
  language?: string | null,
): string {
  return formatMoney(planPrice(plan, currency, cycle), currency, language);
}

/**
 * Anzeigepreis der beiden 9er-Add-ons, gebunden an denselben Waehrungs- und
 * Sprachkontext wie die Band-Karten.
 */
export function addonPriceLabel(currency: 'eur' | 'usd', language: string | null | undefined): string {
  return formatMoney(9, currency, language);
}

/**
 * Empfehlungs-Puffer: das empfohlene Band muss den Produktbestand plus 15 %
 * Wachstumsreserve fassen. 450 Produkte empfehlen deshalb cap1000, nicht
 * cap500 (450 x 1,15 = 517,5 > 500) — wer knapp unter der Kante kauft, steht
 * nach dem naechsten Sync sofort wieder am Limit.
 */
export const RECOMMENDATION_BUFFER = 1.15;

/**
 * Kleinstes OEFFENTLICHES Band, das `totalProducts` samt Puffer fasst und
 * nie kleiner als der aktuelle Tarif ist. `null`, wenn der aktuelle Tarif den
 * Bestand bereits abdeckt — oder wenn es nichts Groesseres mehr zu empfehlen
 * gibt (Legacy-Tarife oberhalb des groessten Bandes). Fallback sonst: das
 * groesste Band.
 */
export function getRecommendedPlan(
  totalProducts: number,
  currentTier: string,
): PlanInfo | null {
  const currentPlan = PLANS_BY_TIER[currentTier];
  if (currentPlan && currentPlan.productLimit >= totalProducts) return null;

  const target = totalProducts * RECOMMENDATION_BUFFER;
  const currentIndex = PLANS.findIndex((p) => p.tier === currentTier);
  const currentLimit = currentPlan?.productLimit ?? 0;

  const candidate = PLANS.find(
    (p, i) =>
      i > currentIndex &&
      isCapTier(p.tier) &&
      p.productLimit >= target &&
      p.productLimit >= currentLimit,
  );
  if (candidate) return candidate;

  // Fallback: das groesste Band — ausser es waere ein Downgrade gegenueber
  // einem (Legacy-)Tarif mit hoeherem Limit; dann gibt es keine Empfehlung.
  const largest = PLANS[PLANS.length - 1];
  return largest.productLimit >= currentLimit ? largest : null;
}

/**
 * Naechst-groesseres oeffentliches Band. Fuer Legacy-Tiers: das kleinste Band
 * mit `productLimit` ueber dem aktuellen Limit (enterprise/agency liegen
 * oberhalb aller Baender → `null`).
 */
export function getNextPlan(currentTier: string): PlanInfo | null {
  const idx = PLANS.findIndex((p) => p.tier === currentTier);
  if (idx >= 0) {
    return idx < PLANS.length - 1 ? PLANS[idx + 1] : null;
  }
  const legacy = PLANS_BY_TIER[currentTier];
  if (!legacy) return null;
  return PLANS.find((p) => isCapTier(p.tier) && p.productLimit > legacy.productLimit) ?? null;
}

/** Wie viele Baender der Kapazitaets-Dialog anbietet. */
export const MODAL_VISIBLE_BANDS = 3;

/**
 * Baender fuer den Kapazitaets-Dialog: ab dem empfohlenen, IMMER drei. Am
 * oberen Ende rueckt das Fenster nach unten (UX-Audit A7O-06: vorher stand
 * fuer 811.414 Produkte genau ein Band da).
 */
export function bandOptionsFor(effective: PlanInfo | null): PlanInfo[] {
  if (!effective) return [];
  const capPlans = PLANS.filter((p) => isCapTier(p.tier));
  let start = capPlans.findIndex((p) => p.tier === effective.tier);
  if (start < 0) {
    start = capPlans.findIndex((p) => p.productLimit >= effective.productLimit);
    if (start < 0) start = capPlans.length - 1;
  }
  start = Math.max(0, Math.min(start, capPlans.length - MODAL_VISIBLE_BANDS));
  return capPlans.slice(start, start + MODAL_VISIBLE_BANDS);
}

/** Deckt selbst das groesste Band den Bestand samt Puffer nicht ab? */
export function exceedsLargestBand(totalProducts: number): boolean {
  const largest = PLANS[PLANS.length - 1];
  return totalProducts * RECOMMENDATION_BUFFER > largest.productLimit;
}
